import React, { useState, useRef, useMemo } from 'react';
import { AutoComplete, Input, Tag } from 'antd';
import { SearchOutlined, AppstoreOutlined, CustomerServiceOutlined, YoutubeOutlined, BankOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const typeColor = { Song: 'purple', Channel: 'red', Company: 'blue', Section: 'green', 'Sub-section': 'cyan' };
const typeBg = { Song: '#f9f0ff', Channel: '#fff1f0', Company: '#e6f4ff', Section: '#f6ffed', 'Sub-section': '#e6fffb' };
const typeIcon = (r, menuIcon) => {
  if (r.type === 'Song') return <CustomerServiceOutlined />;
  if (r.type === 'Channel') return <YoutubeOutlined />;
  if (r.type === 'Company') return <BankOutlined />;
  return menuIcon || <AppstoreOutlined />;
};

// Sub-sections that live inside a page (tabs / cards). `parent` = the sidebar route they belong to;
// the entry is shown only when the user actually has that parent in their menu.
const SUB_SECTIONS = [
  { parent: '/store-links', link: '/store-links?tab=single', label: 'Ek Gaana (Find Store Links)', keywords: 'store link single song' },
  { parent: '/store-links', link: '/store-links?tab=catalog', label: 'Song Data (Store Links)', keywords: 'song data catalog metadata isrc upc' },
  { parent: '/store-links', link: '/store-links?tab=bulk', label: 'Bulk Store Links', keywords: 'bahut saare bulk' },
  { parent: '/store-links', link: '/store-links?tab=saved', label: 'Saved Store Links', keywords: 'saved links' },
  { parent: '/store-links', link: '/store-links?tab=short', label: 'Short Links', keywords: 'bainslamusic short url' },
  { parent: '/settings', link: '/settings?section=backup', label: 'Backup Status', keywords: 'backup disk database drive' },
  { parent: '/settings', link: '/settings?section=server-monitor', label: 'Server Monitor', keywords: 'cpu ram disk bandwidth vps' },
  { parent: '/settings', link: '/settings?section=performance', label: 'Staff Performance', keywords: 'rating review' },
  { parent: '/settings', link: '/settings?section=artists', label: 'Artist Dashboard', keywords: 'artists singers' },
  { parent: '/settings', link: '/settings?section=calendar', label: 'Release Calendar', keywords: 'upcoming release' },
  { parent: '/settings', link: '/settings?section=channel', label: 'Channel Activity', keywords: 'youtube channel activity log' },
  { parent: '/settings', link: '/settings?section=royalties', label: 'Royalty / Payouts', keywords: 'royalty payout' },
  { parent: '/settings', link: '/settings?section=activity-log', label: 'Activity Log', keywords: 'audit history delete edit' },
  { parent: '/settings', link: '/settings?section=monthly-report', label: 'Monthly Report', keywords: 'month summary report' },
  { parent: '/settings', link: '/settings?section=gsheet', label: 'Google Sheet Sync', keywords: 'gsheet google sheet sync' },
  { parent: '/settings', link: '/settings', label: 'Profile / Change Password', keywords: 'profile password install app' },
  { parent: '/users', link: '/users', label: 'Edit Access (User Permissions)', keywords: 'permission access role user staff create' },
  { parent: '/staff-attendance', link: '/staff-attendance', label: 'Attendance Excel / PDF Download', keywords: 'attendance download excel pdf month' },
  { parent: '/staff-salary', link: '/staff-salary', label: 'Advance / Loan (Salary Hisab)', keywords: 'advance loan salary hisab payment' },
  { parent: '/song-metadata', link: '/song-metadata', label: 'Song Metadata Import (Excel/CSV)', keywords: 'isrc upc import csv excel metadata' },
  { parent: '/songs', link: '/songs', label: 'Song Library Sections / Audio Upload', keywords: 'audio mp3 wav upload devotional remix section' },
  { parent: '/thumbnails', link: '/thumbnails', label: 'Thumbnail Bulk Upload', keywords: 'poster thumbnail bulk upload' },
  { parent: '/claim-links', link: '/claim-links', label: 'Ditto Claim Release', keywords: 'ditto claim release youtube link' },
  { parent: '/trending', link: '/trending', label: 'Trending Categories / Search', keywords: 'youtube trending viral tags hashtags' },
  { parent: '/video-tracker', link: '/video-tracker', label: 'First Time Use (Video Tracker)', keywords: 'first time use bm code excel' },
  { parent: '/client-payments', link: '/client-payments', label: 'Client & Vendor Hisab', keywords: 'client vendor hisab cms paid received' },
  { parent: '/ai-center', link: '/ai-center', label: 'AI Maker (Excel / PDF)', keywords: 'ai excel pdf image generator' },
];

const textOf = (label) => {
  if (typeof label === 'string') return label;
  if (Array.isArray(label)) return label.map(textOf).join(' ');
  if (label && label.props) return textOf(label.props.children);
  return '';
};

function flattenMenu(items, out = []) {
  (items || []).forEach((it) => {
    if (!it) return;
    const label = textOf(it.label).trim();
    if (it.children && it.children.length) flattenMenu(it.children, out);
    else if (typeof it.key === 'string' && it.key.startsWith('/') && label) out.push({ link: it.key, label, icon: it.icon });
  });
  return out;
}

const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9\u0900-\u097f]+/g, ' ').trim();

// Top-bar global search: dashboard sections + sub-sections (instant), plus songs / channels / companies (API).
export default function GlobalSearch({ menuItems }) {
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState('');
  const navigate = useNavigate();
  const timer = useRef(null);

  const sections = useMemo(() => {
    const menu = flattenMenu(menuItems);
    const have = new Map(menu.map((m) => [m.link, m]));
    const subs = SUB_SECTIONS.filter((s) => have.has(s.parent));
    return [
      ...menu.map((m) => ({ type: 'Section', title: m.label, link: m.link, icon: m.icon, hay: norm(m.label) })),
      ...subs.map((s) => ({ type: 'Sub-section', title: s.label, link: s.link, icon: have.get(s.parent)?.icon, subtitle: have.get(s.parent)?.label, hay: norm(`${s.label} ${s.keywords}`) })),
    ];
  }, [menuItems]);

  const matchSections = (q) => {
    const words = norm(q).split(' ').filter(Boolean);
    if (!words.length) return [];
    return sections
      .map((s) => {
        const hit = words.every((w) => s.hay.includes(w));
        if (!hit) return null;
        const score = (s.hay.startsWith(words[0]) ? 2 : 0) + (s.type === 'Section' ? 1 : 0);
        return { ...s, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  };

  const toOption = (r, i) => ({
    value: `${r.type}-${r.link || r.id}-${i}`,
    link: r.link,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 4px' }}>
        <span style={{
          width: 44, height: 44, borderRadius: 12, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, color: '#4c51bf', background: typeBg[r.type] || '#f5f5f5', border: '1px solid #e8e8ef',
        }}>
          {typeIcon(r, r.icon)}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1f1f3a', lineHeight: 1.3, whiteSpace: 'normal' }}>{r.title}</div>
          <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color={typeColor[r.type] || 'default'} style={{ margin: 0, fontSize: 12, lineHeight: '20px' }}>{r.type}</Tag>
            {r.subtitle ? <span style={{ color: '#8c8c8c', fontSize: 13 }}>{r.subtitle}</span> : null}
          </div>
        </span>
        <RightOutlined style={{ color: '#bfbfbf', fontSize: 14 }} />
      </div>
    ),
  });

  const handleSearch = (q) => {
    setValue(q);
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 1) { setOptions([]); return; }
    const secs = matchSections(q).map(toOption);
    setOptions(secs);
    if (q.trim().length < 2) return;
    timer.current = setTimeout(async () => {
      try {
        const res = await api.get('/search', { params: { q } });
        const results = res.data?.results || [];
        setOptions([...secs, ...results.map((r, i) => toOption(r, secs.length + i))]);
      } catch (err) { /* sections already shown */ }
    }, 300);
  };

  const go = (link) => {
    if (link) navigate(link);
    setValue('');
    setOptions([]);
  };

  const handleSelect = (_, option) => go(option?.link);

  return (
    <AutoComplete
      value={value}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      style={{ width: 380 }}
      popupMatchSelectWidth={520}
      listHeight={520}
      popupClassName="global-search-popup"
    >
      <Input
        size="large"
        prefix={<SearchOutlined style={{ fontSize: 18, color: '#667eea' }} />}
        placeholder="Section / song / channel search karo… (jaise: Song Agreements, Settings)"
        allowClear
        onPressEnter={() => { if (options[0]?.link) go(options[0].link); }}
      />
    </AutoComplete>
  );
}
